import { useContext, useState } from 'react';
import type { CartLineItem, Pricing } from '@/types/checkout';
import { CartIcon } from './CartIcon';
import { CheckoutContext } from '@/context/CheckoutContext';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { DETAILS_STYLES, TEMPLATE_CLASSES } from '@/constants/styles';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { cn } from '@/utils/cn';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { useHasDetailsBackground } from '@/hooks/useDetailsContentColor';
import { getOfferOverflowCount, getOfferStackPreview, getOffersPaneSurfaceClass } from '@/utils/offers';
import { ProfileCircle } from '../PlayerDetails/ProfileCircle';
import { OfferThumbnailStack } from './OfferThumbnailStack';
import { OffersList } from './OffersList';

/**
 * Layout variants:
 *
 * - `collapsible` (default): compact header with a thumbnail stack + chevron;
 *   the offer list is hidden until the shopper taps. Used by portrait mobile
 *   and desktop multi-offer.
 * - `expanded`: header shows the publisher logo + a small cart icon + "N Offers"
 *   label; the offer list is always visible with no chevron or thumbnail stack.
 *   Used by landscape mobile where vertical space is scarce and the pane sits
 *   above `PromoCode` / `PriceDetails` in a single Details column.
 */
export type OffersPaneVariant = 'collapsible' | 'expanded';

/** Shape shared by both variant subcomponents below. */
type OffersPaneVariantProps = {
  lineItems: CartLineItem[];
  pricing: Pricing;
  surfaceClassName: string;
};

/**
 * Dispatcher — owns the `expanded` state that must survive a variant swap
 * when the shopper rotates between portrait and landscape (would otherwise
 * reset on remount), reads `pricing` from context, and picks the variant
 * subcomponent below.
 */
export const OffersPane = ({
  lineItems,
  variant = 'collapsible',
}: {
  lineItems: CartLineItem[];
  variant?: OffersPaneVariant;
}) => {
  const { pricing } = useContext(CheckoutContext);
  const [expanded, setExpanded] = useState(false);
  const hasPublisherBackground = useHasDetailsBackground();
  const surfaceClassName = getOffersPaneSurfaceClass(hasPublisherBackground);

  const variantProps: OffersPaneVariantProps = { lineItems, pricing, surfaceClassName };

  if (variant === 'expanded') {
    return <ExpandedOffersPane {...variantProps} />;
  }

  return <CollapsibleOffersPane {...variantProps} expanded={expanded} onExpandedChange={setExpanded} />;
};

/**
 * Portrait mobile + desktop variant: compact tappable header with a thumbnail
 * stack + chevron; the offer list is revealed through the grid-rows collapse
 * animation on click. `expanded` is lifted to the parent so it survives a
 * variant swap (rotation); mixpanel tracking stays local so the dispatcher
 * doesn't need `CheckoutContext` for the toggle event.
 */
const CollapsibleOffersPane = ({
  lineItems,
  pricing,
  surfaceClassName,
  expanded,
  onExpandedChange,
}: OffersPaneVariantProps & {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) => {
  const context = useContext(CheckoutContext);

  const previewLineItems = getOfferStackPreview(lineItems);
  const overflowCount = getOfferOverflowCount(lineItems);

  const handleToggle = () => {
    const nextExpanded = !expanded;
    onExpandedChange(nextExpanded);
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.CHECKOUT_SECTION_STATE_CHANGED, {
      ...(properties as Record<string, unknown>),
      type: nextExpanded ? 'offers_expanded' : 'offers_collapsed',
      offers_count: lineItems.length,
    });
  };

  return (
    <div
      data-testid="offers-pane"
      data-variant="collapsible"
      data-state={expanded ? 'expanded' : 'collapsed'}
      className={cn('w-full cursor-pointer rounded-[7px] p-2', surfaceClassName)}
      onClick={handleToggle}
    >
      <div
        className={cn(
          'flex items-center gap-2 transition-[border-color,padding-bottom] duration-300 ease-in-out',
          expanded ? 'border-b border-[#dedede] pb-2' : 'border-b border-transparent pb-0'
        )}
      >
        <div className="relative flex h-10 shrink-0 items-center">
          {!expanded ? (
            <OfferThumbnailStack lineItems={previewLineItems} overflowCount={overflowCount} />
          ) : (
            <div
              className="flex size-10 items-center justify-center rounded border border-black/20 bg-black/15"
              data-testid="offers-expanded-header-icon"
            >
              <CartIcon className="size-4" />
            </div>
          )}
        </div>
        <div className="flex h-10 min-w-0 flex-1 items-center justify-between gap-2">
          <CollapsibleHeaderLabel count={lineItems.length} expanded={expanded} />
          <FontAwesomeIcon
            icon="fa-regular fa-chevron-down"
            data-testid="offers-pane-chevron"
            data-state={expanded ? 'expanded' : 'collapsed'}
            className={cn(
              'shrink-0 transition-transform duration-300 ease-in-out',
              expanded ? 'rotate-180' : 'rotate-0'
            )}
          />
        </div>
      </div>

      <div
        className={cn(
          DETAILS_STYLES.collapsibleBase,
          expanded ? TEMPLATE_CLASSES.collapsibleOpen : TEMPLATE_CLASSES.collapsibleClosed
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <OffersList lineItems={lineItems} pricing={pricing} />
        </div>
      </div>
    </div>
  );
};

/**
 * Landscape mobile variant: header row (`ProfileCircle` + small cart icon +
 * "N Offers") sits above a gray-boxed offer list. No toggle, no chevron, no
 * thumbnail stack — the pane is a static readout of the offers the shopper
 * is buying.
 */
const ExpandedOffersPane = ({ lineItems, pricing, surfaceClassName }: OffersPaneVariantProps) => {
  const { t } = useTranslate();
  return (
    <div
      data-testid="offers-pane"
      data-variant="expanded"
      data-state="expanded"
      className="flex w-full flex-col gap-2.5"
    >
      <div className="flex h-10 items-center justify-between gap-2">
        <ProfileCircle />
        <div className="flex items-center gap-1.5" data-testid="offers-expanded-header-icon">
          <CartIcon className="size-3.5" />
          <span className="text-xs font-semibold">{t('offersCount', { count: lineItems.length })}</span>
        </div>
      </div>
      <div className={cn('w-full rounded-[7px] p-2', surfaceClassName)}>
        <OffersList lineItems={lineItems} pricing={pricing} />
      </div>
    </div>
  );
};

/**
 * Header label stack inside {@link CollapsibleOffersPane} — the "N Offers"
 * title with a "Tap to view details" subtitle that fades out on expand
 * (both `max-height` and `opacity` animate so the row height collapses
 * cleanly, not just the text opacity).
 */
const CollapsibleHeaderLabel = ({ count, expanded }: { count: number; expanded: boolean }) => {
  const { t } = useTranslate();
  return (
    <div className="flex min-w-0 flex-col justify-center overflow-hidden">
      <div className="truncate text-sm font-semibold">{t('offersCount', { count })}</div>
      <div
        className={cn(
          'truncate text-[11px] font-medium opacity-70 transition-all duration-300 ease-in-out',
          expanded ? 'max-h-0 opacity-0' : 'max-h-4 opacity-70'
        )}
      >
        {t('tapToViewOfferDetails')}
      </div>
    </div>
  );
};
